import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { trpc } from '@/lib/trpc';
import { useToast } from '@/contexts/ToastContext';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/Button';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { StockCountModal } from '@/components/count/StockCountModal';
import { ConfirmCountModal } from '@/components/count/ConfirmCountModal';
import { SalesModal } from '@/components/sales/SalesModal';
import { Modal } from '@/components/ui/Modal';
import { RestockForm } from '@/components/restock/RestockForm';
import { DailyTaskCard } from '@/components/today/DailyTaskCard';
import { InlineRestockForm } from '@/components/today/InlineRestockForm';
import { VariancePreviewCard } from '@/components/today/VariancePreviewCard';
import { PendingDraftsCard } from '@/components/today/PendingDraftsCard';
import { TodayRestocksCard } from '@/components/today/TodayRestocksCard';
import { TimeBasedHelper } from '@/components/count/TimeBasedHelper';
import { cn, getSouthAfricanDate, isAccumulatingProduct } from '@/lib/utils';
import { haptic } from '@/lib/haptics';
import { getDefaultUnitType } from '@/lib/constants';

type CountType = 'MORNING' | 'EVENING' | null;

interface CountValidation {
  productId: string;
  productName: string;
  severity: 'ERROR' | 'WARNING';
  type: 'EXCEEDS_STOCK' | 'INCOMPLETE' | 'UNCHANGED' | 'ZERO';
  message: string;
  value: number;
  originalValue?: number;
  maxAllowed?: number;
}

export function TodayPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const utils = trpc.useContext();
  const { user, userBusiness, staffMember } = useAuth();
  const userRole = userBusiness?.role || 'STAFF';
  // Only ADMIN and SUPER_ADMIN can record sales
  const isAdmin = userRole === 'ADMIN' || userRole === 'SUPER_ADMIN';

  // Auto-return from product creation during restock
  const [restockProductId, setRestockProductId] = useState<string | null>(location.state?.restockProductId || null);
  // Clear location state so it doesn't persist on refresh
  React.useEffect(() => {
    if (location.state?.restockProductId) {
      window.history.replaceState({}, '');
    }
  }, []);

  // Date selector - default to today (SA time), can go back 7 days
  const [selectedDate, setSelectedDate] = useState<string>(getSouthAfricanDate(0));
  const isToday = selectedDate === getSouthAfricanDate(0);

  const [activeCountModal, setActiveCountModal] = useState<CountType>(null);
  const [isSalesModalOpen, setIsSalesModalOpen] = useState(false);
  const [isRestockModalOpen, setIsRestockModalOpen] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  // Step 1: Pre-fill tracking
  const [originalPreFills, setOriginalPreFills] = useState<Record<string, number>>({});
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingCounts, setPendingCounts] = useState<Record<string, number>>({});
  const [countWarnings, setCountWarnings] = useState<CountValidation[]>([]);
  const [editMetadata, setEditMetadata] = useState<{ reason: string; originalCount: { performedBy: string | null } } | null>(null);
  const [pendingCountType, setPendingCountType] = useState<'MORNING' | 'EVENING' | null>(null);

  // Today's date for max constraint
  const todayDateStr = getSouthAfricanDate(0);

  const { data: products, isLoading, error, refetch } = trpc.product.list.useQuery();
  const { data: morningCounts, isLoading: morningLoading } = trpc.stock.getCountsByDate.useQuery({ date: selectedDate, type: 'MORNING' });
  const { data: eveningCounts, isLoading: eveningLoading } = trpc.stock.getCountsByDate.useQuery({ date: selectedDate, type: 'EVENING' });
  const { data: todayReUps, isLoading: reUpsLoading } = trpc.stock.getReUps.useQuery({ date: selectedDate });
  const { data: todaySales, isLoading: salesLoading } = trpc.sale.getByDate.useQuery({ date: selectedDate });

  // Historical mode: check if previous day has evening counts
  const { data: prevEveningData } = trpc.stock.getPreviousEveningCounts.useQuery(
    { date: selectedDate },
    { enabled: !isToday }
  );
  const isDisconnected = !isToday && prevEveningData?.isConnected === false;
  const recordCount = trpc.stock.recordCount.useMutation();
  const createReUp = trpc.stock.createReUp.useMutation();
  const createSale = trpc.sale.create.useMutation();
  const updateSale = trpc.sale.update.useMutation();
  const deleteSale = trpc.sale.delete.useMutation();
  const deleteCountsByDate = trpc.stock.deleteCountsByDate.useMutation();
  const deleteReUpsByDate = trpc.stock.deleteReUpsByDate.useMutation();
  const deleteSalesByDate = trpc.sale.deleteSalesByDate.useMutation();
  const createProduct = trpc.product.create.useMutation();

  // Draft workflow mutations and queries
  const { data: morningDrafts } = trpc.stock.getDraftCounts.useQuery({
    date: selectedDate,
    type: 'MORNING'
  });
  const { data: eveningDrafts } = trpc.stock.getDraftCounts.useQuery({
    date: selectedDate,
    type: 'EVENING'
  });
  const saveDraftCounts = trpc.stock.saveDraftCounts.useMutation();
  const confirmCounts = trpc.stock.confirmCounts.useMutation();
  const rejectDrafts = trpc.stock.rejectDrafts.useMutation();

  // Determine completion status from actual database data
  const morningCompleted = (morningCounts?.length ?? 0) > 0;
  const restockCompleted = (todayReUps?.length ?? 0) > 0;
  const eveningCompleted = (eveningCounts?.length ?? 0) > 0;
  const salesCompleted = (todaySales?.length ?? 0) > 0;

  // Draft status for pending review
  const morningHasPendingDrafts = (morningDrafts?.length ?? 0) > 0;
  const eveningHasPendingDrafts = (eveningDrafts?.length ?? 0) > 0;

  // Calculate expected sales from morning count - evening count
  const calculateExpectedSales = () => {
    if (!morningCounts || !eveningCounts || !products) return 0;

    // Create maps for quick lookup
    const morningMap = new Map(morningCounts.map(c => [c.productId, c.quantity]));
    const eveningMap = new Map(eveningCounts.map(c => [c.productId, c.quantity]));

    let expectedTotal = 0;

    products.forEach(product => {
      const morningQty = morningMap.get(product.id) || 0;
      const eveningQty = eveningMap.get(product.id) || 0;
      const soldQty = morningQty - eveningQty;

      if (soldQty > 0) {
        expectedTotal += soldQty * product.pricePerUnit;
      }
    });

    return expectedTotal;
  };

  const expectedSales = calculateExpectedSales();

  // Step 1: Pre-fill calculation function
  const openCountModal = (type: 'MORNING' | 'EVENING') => {
    if (!products) return;

    // For evening counts, ensure morning counts and restocks are loaded
    if (type === 'EVENING' && (morningLoading || reUpsLoading)) {
      toast.info('Loading historical data, please wait...');
      return;
    }

    // Calculate pre-fills based on type and historical mode
    let preFills: Record<string, number> = {};

    if (type === 'MORNING') {
      if (isToday) {
        // Today: use currentStock (reflects last evening count)
        preFills = products.reduce((acc, product) => {
          acc[product.id] = product.currentStock;
          return acc;
        }, {} as Record<string, number>);
      } else if (isDisconnected) {
        // Disconnected past date: no pre-fills (user adds products manually)
        preFills = {};
      } else {
        // Connected past date: use previous day's evening counts
        preFills = products.reduce((acc, product) => {
          const prevEvening = prevEveningData?.counts.find(c => c.productId === product.id);
          if (prevEvening) {
            acc[product.id] = prevEvening.quantity;
          } else {
            acc[product.id] = 0;
          }
          return acc;
        }, {} as Record<string, number>);
      }
    } else {
      // EVENING pre-fills
      if (isToday) {
        // Today: morning count + restocks, fallback to currentStock
        preFills = products.reduce((acc, product) => {
          const morningCount = morningCounts?.find(c => c.productId === product.id);
          const productRestocks = todayReUps?.filter(r => r.productId === product.id) || [];
          const restockTotal = productRestocks.reduce((sum, r) => sum + r.quantity, 0);
          if (morningCount) {
            acc[product.id] = morningCount.quantity + restockTotal;
          } else {
            acc[product.id] = product.currentStock;
          }
          return acc;
        }, {} as Record<string, number>);
      } else {
        // Historical: derive pre-fills from morning count records (includes inactive products)
        if (morningCounts) {
          morningCounts.forEach(c => {
            const productRestocks = todayReUps?.filter(r => r.productId === c.productId) || [];
            const restockTotal = productRestocks.reduce((sum, r) => sum + r.quantity, 0);
            preFills[c.productId] = c.quantity + restockTotal;
          });
        }
      }
    }

    setOriginalPreFills(preFills);
    setIsEditMode(false);
    setActiveCountModal(type);
  };

  // Step 2: Evening count stock validation
  function validateEveningStockLimits(
    counts: Record<string, number>,
    morningCountsData: typeof morningCounts,
    reUps: typeof todayReUps,
    productsList: typeof products
  ): CountValidation[] {
    if (!productsList || !morningCountsData) return [];

    const errors: CountValidation[] = [];

    // Build maps for quick lookup
    const morningMap = new Map(morningCountsData.map(c => [c.productId, c.quantity]));
    const reUpMap = new Map<string, number>();

    reUps?.forEach(reUp => {
      const current = reUpMap.get(reUp.productId) || 0;
      reUpMap.set(reUp.productId, current + reUp.quantity);
    });

    Object.entries(counts).forEach(([productId, eveningQty]) => {
      const product = productsList.find(p => p.id === productId);
      if (!product) return;

      const morningQty = morningMap.get(productId) || 0;
      const reUpQty = reUpMap.get(productId) || 0;
      const maxAllowed = morningQty + reUpQty;

      // CRITICAL ERROR: Evening count exceeds morning + reups
      // EXCEPTION: Accumulating products (like STEMS) can exceed because they build up throughout the day
      const isAccumulating = product.isAccumulating || isAccumulatingProduct(product.name);
      if (eveningQty > maxAllowed && !isAccumulating) {
        errors.push({
          productId,
          productName: product.name,
          severity: 'ERROR',
          type: 'EXCEEDS_STOCK',
          message: `Cannot exceed ${maxAllowed}g (${morningQty}g morning + ${reUpQty}g reup)`,
          value: eveningQty,
          originalValue: morningQty,
          maxAllowed
        });
      }
    });

    return errors;
  }

  // Step 2B: Morning count variance validation
  function validateMorningCountLimits(
    counts: Record<string, number>,
    productsList: typeof products,
    reUps: typeof todayReUps
  ): CountValidation[] {
    if (!productsList) return [];

    const errors: CountValidation[] = [];

    // Build reup map
    const reUpMap = new Map<string, number>();
    reUps?.forEach(reUp => {
      const current = reUpMap.get(reUp.productId) || 0;
      reUpMap.set(reUp.productId, current + reUp.quantity);
    });

    Object.entries(counts).forEach(([productId, morningQty]) => {
      const product = productsList.find(p => p.id === productId);
      if (!product) return;

      // Use currentStock as the baseline (this reflects the last evening count)
      const prevEveningQty = product.currentStock;
      const reUpQty = reUpMap.get(productId) || 0;
      const expectedMorning = prevEveningQty + reUpQty;

      // Calculate variance
      const variance = morningQty - expectedMorning;
      const variancePercentage = expectedMorning === 0
        ? (variance === 0 ? 0 : 100)
        : Math.abs((variance / expectedMorning) * 100);

      // For historical data entry, show warnings instead of errors
      // Only show as warnings, not blocking errors
      if (Math.abs(variance) > 0.01) { // Allow tiny rounding errors only
        if (variance > 0) {
          // Stock increased overnight - show as warning only
          errors.push({
            productId,
            productName: product.name,
            severity: 'WARNING',
            type: 'EXCEEDS_STOCK',
            message: `Morning count (${morningQty}g) exceeds expected (${prevEveningQty}g) ${reUpQty > 0 ? `+ reup (${reUpQty}g)` : ''} by ${variance.toFixed(2)}g`,
            value: morningQty,
            originalValue: prevEveningQty,
            maxAllowed: expectedMorning
          });
        } else {
          // Stock decreased overnight - show as warning only
          errors.push({
            productId,
            productName: product.name,
            severity: 'WARNING',
            type: 'EXCEEDS_STOCK',
            message: `Morning count (${morningQty}g) is ${Math.abs(variance).toFixed(2)}g less than expected (${prevEveningQty}g) ${reUpQty > 0 ? `+ reup (${reUpQty}g)` : ''}`,
            value: morningQty,
            originalValue: prevEveningQty,
            maxAllowed: expectedMorning
          });
        }
      }
    });

    return errors;
  }

  // Step 3: Updated validation function with completeness check
  function validateAllCounts(
    counts: Record<string, number>,
    preFills: Record<string, number>,
    productsList: typeof products
  ): CountValidation[] {
    if (!productsList) return [];

    const validations: CountValidation[] = [];

    // Check 1: Completeness - ALL products must be entered
    const missingProducts = productsList.filter(p => {
      // Exception: Pre-filled with 0 can stay 0
      const preFill = preFills[p.id];
      const entered = counts[p.id];

      // Missing if: no entry AND pre-fill is not 0
      return (entered === undefined || entered === null || isNaN(entered)) && preFill !== 0;
    });

    if (missingProducts.length > 0) {
      missingProducts.forEach(product => {
        validations.push({
          productId: product.id,
          productName: product.name,
          severity: 'ERROR',
          type: 'INCOMPLETE',
          message: 'Entry required',
          value: 0,
          originalValue: preFills[product.id]
        });
      });
    }

    // Check 2: Existing warnings (unchanged, zero) - now marked as WARNING severity
    Object.entries(counts).forEach(([productId, quantity]) => {
      const product = productsList.find(p => p.id === productId);
      if (!product) return;

      const preFilledValue = preFills[productId];

      // Flag unchanged from pre-fill (only if not zero)
      if (preFilledValue !== undefined && quantity === preFilledValue && preFilledValue !== 0) {
        validations.push({
          productId,
          productName: product.name,
          severity: 'WARNING',
          type: 'UNCHANGED',
          message: `Unchanged from pre-filled value (${quantity}g)`,
          value: quantity,
          originalValue: preFilledValue
        });
      }

      // Flag zero counts (only if not pre-filled with 0)
      if (quantity === 0 && preFilledValue !== 0) {
        validations.push({
          productId,
          productName: product.name,
          severity: 'WARNING',
          type: 'ZERO',
          message: 'Count is zero',
          value: 0,
          originalValue: preFilledValue
        });
      }
    });

    return validations;
  }

  // Step 4: Handler for when counts are ready (validation + conditional confirmation)
  const handleCountsReady = async (counts: Record<string, number>) => {
    if (!products || !activeCountModal) return;

    const currentType = activeCountModal;

    // For disconnected dates, skip completeness check (user selects products)
    // For connected/today, run full validation
    let validations: CountValidation[] = [];
    if (!isDisconnected) {
      validations = validateAllCounts(counts, originalPreFills, products);
    }

    // For EVENING counts, add stock limit validation
    if (currentType === 'EVENING') {
      // Skip validation if data is still loading
      if (morningLoading || reUpsLoading) {
        toast.error('Historical data is still loading. Please try again.');
        setShowConfirmModal(false);
        return;
      }

      const stockErrors = validateEveningStockLimits(
        counts,
        morningCounts,
        todayReUps,
        products
      );
      validations = [...validations, ...stockErrors];
    }

    // For MORNING counts, add variance validation (skip for disconnected - no previous evening to compare)
    if (currentType === 'MORNING' && !isDisconnected) {
      const varianceErrors = validateMorningCountLimits(
        counts,
        products,
        todayReUps
      );
      validations = [...validations, ...varianceErrors];
    }

    // Separate errors and warnings
    const errors = validations.filter(v => v.severity === 'ERROR');
    const warnings = validations.filter(v => v.severity === 'WARNING');

    if (errors.length > 0) {
      // HARD BLOCK: Show errors, prevent save
      setCountWarnings(validations);  // Pass all for display
      setPendingCounts(counts);
      setPendingCountType(currentType);
      setShowConfirmModal(true);
    } else if (warnings.length > 0) {
      // Soft warnings - can acknowledge and save
      setCountWarnings(validations);
      setPendingCounts(counts);
      setPendingCountType(currentType);
      setShowConfirmModal(true);
    } else {
      // No issues - save directly
      await handleConfirmSave(counts);
    }
  };

  // Step 4: Confirm save handler (called after validation or confirmation)
  const handleConfirmSave = async (counts: Record<string, number>) => {
    if (!activeCountModal && !showConfirmModal) return;

    // Determine the count type
    const type = activeCountModal || pendingCountType;
    if (!type) return;

    try {
      // If in edit mode, delete old counts first
      if (isEditMode) {
        await deleteCountsByDate.mutateAsync({ date: selectedDate, type });
      }

      if (isAdmin) {
        // Admin: Direct save (existing flow)
        const countsArray = Object.entries(counts).map(([productId, quantity]) => ({
          productId,
          type,
          quantity,
          date: selectedDate,
          performedBy: staffMember || undefined,
        }));

        await recordCount.mutateAsync({
          counts: countsArray,
          metadata: isEditMode && editMetadata ? {
            isEdit: true,
            reason: editMetadata.reason,
            originalCount: editMetadata.originalCount,
          } : undefined,
        });
        toast.success(`${type === 'MORNING' ? 'Morning' : 'Evening'} count ${isEditMode ? 'updated' : 'saved'} successfully!`);
      } else {
        // Staff: Save as draft for admin approval
        const countsArray = Object.entries(counts).map(([productId, quantity]) => ({
          productId,
          quantity,
          warnings: countWarnings
            .filter(w => w.productId === productId && w.severity === 'WARNING')
            .map(w => w.type),
        }));

        await saveDraftCounts.mutateAsync({
          date: selectedDate,
          type,
          counts: countsArray,
          performedBy: staffMember || undefined,
        });
        toast.success(`${type === 'MORNING' ? 'Morning' : 'Evening'} count submitted for review!`);
      }

      // Reset state
      setShowConfirmModal(false);
      setActiveCountModal(null);
      setPendingCounts({});
      setCountWarnings([]);
      setPendingCountType(null);
      setEditMetadata(null);
      setIsEditMode(false);

      // Refetch counts and products to get updated data
      utils.stock.getCountsByDate.invalidate();
      utils.stock.getDraftCounts.invalidate();
      utils.product.list.invalidate();
    } catch (error) {
      toast.error('Failed to save count. Please try again.');
      console.error('Failed to record count:', error);
    }
  };

  const handleSalesSubmit = async (data: { productSales?: Array<{ productId: string; quantity: number }>; productId?: string; quantity?: number; totalAmount?: number }) => {
    try {
      // If in edit mode, delete existing sales first
      if (salesCompleted && todaySales) {
        await Promise.all(
          todaySales.map((sale) => deleteSale.mutateAsync({ id: sale.id }))
        );
      }

      // Then create new sales (same logic for both create and update)
      if (data.productSales && Array.isArray(data.productSales)) {
        await Promise.all(
          data.productSales.map((sale: { productId: string; quantity: number }) => {
            const product = products?.find(p => p.id === sale.productId);
            return createSale.mutateAsync({
              productId: sale.productId,
              quantity: sale.quantity,
              totalAmount: product ? product.pricePerUnit * sale.quantity : 0,
              performedBy: staffMember || undefined,
              date: selectedDate, // Use the selected date from date picker
            });
          })
        );
      } else if (data.productId) {
        // Single product sale
        await createSale.mutateAsync({
          ...data,
          performedBy: staffMember || undefined,
          date: selectedDate, // Use the selected date from date picker
        });
      }

      toast.success(salesCompleted ? 'Sales updated successfully!' : 'Sales recorded successfully!');
      utils.product.list.invalidate();
      utils.sale.getByDate.invalidate();
      setIsSalesModalOpen(false);
    } catch (error) {
      toast.error(salesCompleted ? 'Failed to update sales. Please try again.' : 'Failed to record sales. Please try again.');
      console.error('Failed to record/update sales:', error);
    }
  };

  // Draft workflow handlers (admin only)
  const handleApproveDrafts = async (type: 'MORNING' | 'EVENING', acknowledgeWarnings: boolean) => {
    try {
      await confirmCounts.mutateAsync({
        date: selectedDate,
        type,
        acknowledgedWarnings: acknowledgeWarnings,
      });

      toast.success(`${type === 'MORNING' ? 'Morning' : 'Evening'} count approved!`);
      utils.stock.getCountsByDate.invalidate();
      utils.stock.getDraftCounts.invalidate();
      utils.product.list.invalidate();
    } catch (error) {
      toast.error('Failed to approve count. Please try again.');
      console.error('Failed to approve drafts:', error);
    }
  };

  const handleRejectDrafts = async (type: 'MORNING' | 'EVENING', reason: string) => {
    try {
      await rejectDrafts.mutateAsync({ date: selectedDate, type, reason });
      toast.success(`${type === 'MORNING' ? 'Morning' : 'Evening'} count rejected: ${reason}`);
      utils.stock.getDraftCounts.invalidate();
    } catch (error) {
      toast.error('Failed to reject count. Please try again.');
      console.error('Failed to reject drafts:', error);
    }
  };

  const handleResetCounts = async () => {
    try {
      await Promise.all([
        deleteCountsByDate.mutateAsync({ date: selectedDate }),
        deleteReUpsByDate.mutateAsync({ date: selectedDate }),
        deleteSalesByDate.mutateAsync({ date: selectedDate }),
      ]);

      toast.success('All counts for selected date have been reset!');
      utils.stock.getCountsByDate.invalidate();
      utils.stock.getReUps.invalidate();
      utils.product.list.invalidate();
      utils.sale.getByDate.invalidate();
      setShowResetConfirm(false);
    } catch (error) {
      toast.error('Failed to reset counts. Please try again.');
      console.error('Failed to reset counts:', error);
    }
  };

  // Step 5: Soft lock with audit trail
  const handleEditCount = (type: CountType) => {
    if (!type || !products) return;

    // Prevent staff from editing previous days
    if (!isAdmin && !isToday) {
      toast.error('Staff members can only edit today\'s counts');
      return;
    }

    // Check if count already exists for this date using the already-fetched data
    const countsForType = type === 'MORNING' ? morningCounts : eveningCounts;
    const existingCount = countsForType && countsForType.length > 0 ? countsForType[0] : null;

    if (existingCount) {
      const performedByName = existingCount.performedBy || 'Unknown';

      // Use the same isAdmin check (based on userBusiness role) for consistency
      if (!isAdmin) {
        // Block non-admins
        toast.error(
          `This count was already saved by ${performedByName}. ` +
          `Contact an admin if a correction is needed.`
        );
        return;
      }

      // Admin - require reason
      const editReason = prompt(
        `⚠️ Warning: This count was already saved by ${performedByName}.\n\n` +
        `Editing will create an audit log entry.\n\n` +
        `Please provide a reason for this edit:`
      );

      if (!editReason || editReason.trim() === '') {
        return; // User cancelled or didn't provide reason
      }

      // Set edit metadata
      setEditMetadata({
        reason: editReason.trim(),
        originalCount: existingCount
      });
    }

    setIsEditMode(true);

    // Calculate pre-fills for edit mode
    const preFills = products.reduce((acc, product) => {
      if (type === 'EVENING') {
        acc[product.id] = product.currentStock;
      } else {
        // Morning: pre-fill with current stock (which was set by last evening count)
        acc[product.id] = product.currentStock;
      }
      return acc;
    }, {} as Record<string, number>);

    setOriginalPreFills(preFills);
    setActiveCountModal(type);
  };

  if (isLoading) return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
      <LoadingSpinner size="lg" />
      <p className="text-gray-400 text-sm">Loading products...</p>
    </div>
  );

  if (error) return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
      <div className="text-6xl">⚠️</div>
      <div className="text-center">
        <h3 className="text-xl font-bold text-white mb-2">Failed to load products</h3>
        <p className="text-gray-400 text-sm mb-4">
          {error.message || 'Please check your connection and try again'}
        </p>
        <Button onClick={() => refetch()} variant="primary">
          🔄 Retry
        </Button>
      </div>
    </div>
  );

  if (!products || products.length === 0) return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
      <div className="text-6xl">📦</div>
      <div className="text-center">
        <h3 className="text-xl font-bold text-white mb-2">No products yet</h3>
        <p className="text-gray-400 text-sm mb-4">Add your first product to get started</p>
        <Button onClick={() => navigate('/inventory')} variant="primary">
          ➕ Add Product
        </Button>
      </div>
    </div>
  );

  // Format date for display
  const formatDateLabel = (dateStr: string) => {
    const todayStr = getSouthAfricanDate(0);
    const yesterdayStr = getSouthAfricanDate(1);

    if (dateStr === todayStr) return 'Today';
    if (dateStr === yesterdayStr) return 'Yesterday';

    // Calculate days ago
    const date = new Date(dateStr);
    const today = new Date(todayStr);
    const daysAgo = Math.floor((today.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
    return `${daysAgo} days ago`;
  };

  // Get time of day for greeting
  const getTimeOfDay = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'morning';
    if (hour < 17) return 'afternoon';
    return 'evening';
  };

  return (
    <div className="space-y-4 animate-fade-in">
      {/* Header with greeting */}
      <div className="bg-dark-card rounded-2xl p-5 border-2 border-primary/30 mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-black text-white">Today</h1>
            <p className="text-gray-400">
              {new Date().toLocaleDateString('en-ZA', { weekday: 'long', month: 'long', day: 'numeric' })}
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-400">Good {getTimeOfDay()},</p>
            <p className="text-lg font-bold text-primary">{staffMember || 'Staff'}</p>
          </div>
        </div>
      </div>

      {/* Time-Based Helper */}
      {isToday && (
        <TimeBasedHelper
          morningCompleted={morningCompleted}
          eveningCompleted={eveningCompleted}
        />
      )}

      {/* Date Selector (compact) */}
      {!isToday && (
        <div className="bg-warning/10 border-2 border-warning/30 rounded-xl p-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-warning">⚠️</span>
            <span className="text-sm text-warning font-medium">Viewing {formatDateLabel(selectedDate)}</span>
          </div>
          <button
            onClick={() => { haptic.light(); setSelectedDate(getSouthAfricanDate(0)); }}
            className="px-3 py-1 bg-warning/20 text-warning rounded-lg text-sm font-medium transition-all active:scale-[0.97]"
          >
            Go to Today
          </button>
        </div>
      )}

      {/* Date Picker */}
      <div className="bg-dark-card rounded-xl p-4 border border-primary/20">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-xl">📅</span>
            <div>
              <div className="text-sm text-gray-400">Select Date</div>
              <div className="text-white font-medium">
                {formatDateLabel(selectedDate)}
              </div>
            </div>
          </div>
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => { haptic.light(); setSelectedDate(e.target.value); }}
            max={todayDateStr}
            className="bg-dark-lighter border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:border-primary focus:outline-none transition-colors"
          />
        </div>
      </div>

      {/* Daily Tasks Section */}
      <div className="space-y-4">
        <h2 className="text-lg font-bold text-white px-1">Your Daily Tasks</h2>

        {/* Morning Count Card */}
        <DailyTaskCard
          icon="☀️"
          title="Morning Count"
          status={morningCompleted ? 'done' : morningHasPendingDrafts ? 'pending_review' : 'pending'}
          description={morningCompleted
            ? `Completed by ${morningCounts?.[0]?.performedBy || 'Staff'}`
            : morningHasPendingDrafts
              ? `Submitted by ${morningDrafts?.[0]?.performedBy || 'Staff'} - awaiting approval`
              : 'Count all products when you open'}
          action={!morningCompleted && !morningHasPendingDrafts ? () => openCountModal('MORNING') : undefined}
          onEdit={morningCompleted && (isAdmin || isToday) ? () => handleEditCount('MORNING') : undefined}
        />

        {/* Restocks Card - Expandable with Inline Form */}
        <DailyTaskCard
          icon="📦"
          title="Restocks"
          status={restockCompleted ? 'done' : 'optional'}
          description={restockCompleted
            ? `${todayReUps?.length} item${(todayReUps?.length || 0) > 1 ? 's' : ''} restocked`
            : 'Add any inventory received today'}
          expandable
          defaultExpanded={!!restockProductId}
        >
          <InlineRestockForm
            products={products || []}
            onAddNewProduct={isToday ? () => navigate('/inventory/new', { state: { fromRestock: true } }) : undefined}
            onCreateProduct={!isToday && isAdmin ? async (name: string, category: string, quality?: string) => {
              const product = await createProduct.mutateAsync({
                name,
                category,
                quality,
                unitSize: 1,
                unitType: getDefaultUnitType(category),
                pricePerUnit: 0,
                minStock: 0,
                maxStock: 100,
                isActive: true,
              });
              await utils.product.list.invalidate();
              return {
                id: product.id,
                name: product.name,
                category: product.category,
                currentStock: product.currentStock,
                unitType: product.unitType,
              };
            } : undefined}
            selectedProductId={restockProductId}
            onProductSelected={() => setRestockProductId(null)}
            onSubmit={async (data) => {
              try {
                await createReUp.mutateAsync({
                  productId: data.productId,
                  quantity: data.quantity,
                  performedBy: staffMember || undefined,
                  date: selectedDate,
                });
                toast.success('Restock recorded!');
                haptic.success();
                utils.stock.getReUps.invalidate();
                utils.product.list.invalidate();
              } catch (error) {
                haptic.error();
                toast.error('Failed to record restock');
              }
            }}
            isLoading={createReUp.isPending}
          />
        </DailyTaskCard>

        {/* Today's Restocks Summary */}
        <TodayRestocksCard
          restocks={todayReUps}
          products={products}
          isToday={isToday}
        />

        {/* Evening Count Card */}
        <DailyTaskCard
          icon="🌙"
          title="Evening Count"
          status={eveningCompleted ? 'done' : eveningHasPendingDrafts ? 'pending_review' : !morningCompleted && !morningHasPendingDrafts ? 'locked' : 'pending'}
          description={
            eveningCompleted ? `Completed by ${eveningCounts?.[0]?.performedBy || 'Staff'}` :
            eveningHasPendingDrafts ? `Submitted by ${eveningDrafts?.[0]?.performedBy || 'Staff'} - awaiting approval` :
            !morningCompleted && !morningHasPendingDrafts ? 'Complete morning count first' :
            'Count remaining stock at closing'
          }
          action={!eveningCompleted && !eveningHasPendingDrafts && (morningCompleted || morningHasPendingDrafts) ? () => openCountModal('EVENING') : undefined}
          onEdit={eveningCompleted && (isAdmin || isToday) ? () => handleEditCount('EVENING') : undefined}
        />

        {/* Pending Drafts Section - Admin Only */}
        {isAdmin && morningHasPendingDrafts && !morningCompleted && morningDrafts && (
          <PendingDraftsCard
            type="MORNING"
            drafts={morningDrafts}
            onApprove={(ack) => handleApproveDrafts('MORNING', ack)}
            onReject={(reason) => handleRejectDrafts('MORNING', reason)}
            isApproving={confirmCounts.isPending}
          />
        )}
        {isAdmin && eveningHasPendingDrafts && !eveningCompleted && eveningDrafts && (
          <PendingDraftsCard
            type="EVENING"
            drafts={eveningDrafts}
            onApprove={(ack) => handleApproveDrafts('EVENING', ack)}
            onReject={(reason) => handleRejectDrafts('EVENING', reason)}
            isApproving={confirmCounts.isPending}
          />
        )}
      </div>

      {/* Admin Section */}
      {isAdmin && (
        <div className="mt-8 space-y-4">
          <h2 className="text-lg font-bold text-white px-1 flex items-center gap-2">
            <span className="px-2 py-0.5 bg-primary/20 text-primary text-xs rounded-full">ADMIN</span>
            Management
          </h2>

          {/* Sales Entry */}
          <DailyTaskCard
            icon="💰"
            title="Record Sales"
            status={salesCompleted ? 'done' : 'pending'}
            description={salesCompleted ? 'Sales recorded from POS' : 'Enter sales from POS system'}
            action={() => setIsSalesModalOpen(true)}
          />

          {/* Variance Preview */}
          {eveningCompleted && salesCompleted && products && (
            <VariancePreviewCard
              products={products}
              morningCounts={morningCounts?.map(c => ({ productId: c.productId, quantity: c.quantity })) || []}
              eveningCounts={eveningCounts?.map(c => ({ productId: c.productId, quantity: c.quantity })) || []}
              sales={todaySales?.map(s => ({ productId: s.productId, quantity: s.quantity })) || []}
              restocks={todayReUps?.map(r => ({ productId: r.productId, quantity: r.quantity })) || []}
            />
          )}

          {/* Reset Button */}
          <div className="flex justify-center pt-4">
            <Button
              variant="ghost"
              size="md"
              onClick={() => setShowResetConfirm(true)}
              className="text-gray-500 hover:text-error min-h-[44px]"
            >
              🔄 Reset Day's Data
            </Button>
          </div>
        </div>
      )}

      {/* Reset Confirmation Dialog */}
      {showResetConfirm && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-dark-card rounded-lg p-6 max-w-sm w-full border border-error/50">
            <h3 className="text-xl font-bold text-white mb-2">Reset All Counts?</h3>
            <p className="text-gray-400 text-sm mb-6">
              This will delete all morning counts, evening counts, and restocks for today. This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <Button
                variant="ghost"
                onClick={() => { haptic.light(); setShowResetConfirm(false); }}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={() => { haptic.medium(); handleResetCounts(); }}
                className="flex-1 bg-error hover:bg-error/80"
              >
                Reset All
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <ConfirmCountModal
          isOpen={showConfirmModal}
          onClose={() => {
            // User clicked "Go Back" - close confirmation and reopen count modal
            setShowConfirmModal(false);
            // activeCountModal is still set, so modal will reappear
            // pendingCounts will be used as initialCounts
          }}
          onConfirm={() => handleConfirmSave(pendingCounts)}
          type={pendingCountType || 'MORNING'}
          totalProducts={Object.keys(pendingCounts).length}
          warnings={countWarnings}
          isAdmin={isAdmin}
        />
      )}

      {/* Stock Count Modal - Morning */}
      <StockCountModal
        key="morning-count-modal"
        isOpen={activeCountModal === 'MORNING' && !showConfirmModal}
        onClose={() => {
          setActiveCountModal(null);
          setIsEditMode(false);
          // Don't clear pendingCounts - user may reopen modal
          setCountWarnings([]);
        }}
        type="MORNING"
        products={(products || []).map(p => ({
          id: p.id,
          name: p.name,
          category: p.category,
          currentStock: p.currentStock,
          unitType: p.unitType,
        }))}
        initialCounts={
          isEditMode
            ? morningCounts
            : isDisconnected
              ? undefined // Disconnected: no pre-fills, user adds products
              : isToday
                ? products?.map(p => ({ productId: p.id, quantity: p.currentStock }))
                : prevEveningData?.isConnected
                  ? products?.map(p => {
                      const prevEvening = prevEveningData?.counts.find(c => c.productId === p.id);
                      return { productId: p.id, quantity: prevEvening?.quantity ?? 0 };
                    })
                  : products?.map(p => ({ productId: p.id, quantity: p.currentStock }))
        }
        isEditMode={isEditMode}
        onCountsReady={handleCountsReady}
        originalPreFills={originalPreFills}
        isAdmin={isAdmin}
        isDisconnected={isDisconnected}
        onCreateProduct={isDisconnected && isAdmin ? async (name: string, category: string, quality?: string) => {
          const product = await createProduct.mutateAsync({
            name,
            category,
            quality,
            unitSize: 1,
            unitType: getDefaultUnitType(category),
            pricePerUnit: 0,
            minStock: 0,
            maxStock: 100,
            isActive: true,
          });
          await utils.product.list.invalidate();
          return {
            id: product.id,
            name: product.name,
            category: product.category,
            currentStock: product.currentStock,
            unitType: product.unitType,
          };
        } : undefined}
      />

      {/* Stock Count Modal - Evening */}
      <StockCountModal
        key="evening-count-modal"
        isOpen={activeCountModal === 'EVENING' && !showConfirmModal}
        onClose={() => {
          setActiveCountModal(null);
          setIsEditMode(false);
          // Don't clear pendingCounts - user may reopen modal
          setCountWarnings([]);
        }}
        type="EVENING"
        products={(() => {
          // For historical dates, derive product list from morning count records
          // This ensures inactive (historical-only) products are included
          if (!isToday && morningCounts && morningCounts.length > 0) {
            return morningCounts.map((c: any) => ({
              id: c.product.id,
              name: c.product.name,
              category: c.product.category,
              currentStock: c.product.currentStock,
              unitType: c.product.unitType,
            }));
          }
          return (products || []).map(p => ({
            id: p.id,
            name: p.name,
            category: p.category,
            currentStock: p.currentStock,
            unitType: p.unitType,
          }));
        })()}
        initialCounts={
          isEditMode ? eveningCounts : undefined
        }
        isEditMode={isEditMode}
        onCountsReady={handleCountsReady}
        originalPreFills={originalPreFills}
        isAdmin={isAdmin}
      />

      {/* Sales Modal */}
      <SalesModal
        isOpen={isSalesModalOpen}
        onClose={() => setIsSalesModalOpen(false)}
        products={(() => {
          // For historical dates, derive from count records (includes inactive products)
          if (!isToday) {
            const productMap = new Map<string, any>();
            // Build from morning counts (primary source with product relations)
            morningCounts?.forEach((c: any) => {
              if (c.product) {
                productMap.set(c.product.id, c.product);
              }
            });
            // Add any from evening counts not already present
            eveningCounts?.forEach((c: any) => {
              if (c.product && !productMap.has(c.product.id)) {
                productMap.set(c.product.id, c.product);
              }
            });
            if (productMap.size > 0) {
              return Array.from(productMap.values());
            }
          }
          return products || [];
        })()}
        expectedSales={expectedSales}
        morningCounts={morningCounts || []}
        eveningCounts={eveningCounts || []}
        restocks={todayReUps?.map(r => ({ productId: r.productId, quantity: r.quantity })) || []}
        existingSales={todaySales?.map(s => ({
          id: s.id,
          productId: s.productId,
          quantity: s.quantity,
          totalAmount: s.totalAmount
        })) || []}
        onSubmit={handleSalesSubmit}
        isEditMode={salesCompleted}
      />

      {/* Restock Modal */}
      <Modal
        isOpen={isRestockModalOpen}
        onClose={() => setIsRestockModalOpen(false)}
        title="Add Restock"
        size="lg"
      >
        <RestockForm
          products={products || []}
          onSubmit={async (data) => {
            try {
              const isHistorical = data.date !== getSouthAfricanDate(0);
              const notesWithSupplier = data.supplier ? `Supplier: ${data.supplier}. ${data.notes || ''}` : data.notes;
              await createReUp.mutateAsync({
                productId: data.productId,
                quantity: data.quantity,
                notes: notesWithSupplier,
                performedBy: staffMember || undefined,
                date: data.date,
              });

              if (isHistorical) {
                toast.success('Historical restock recorded! Stock levels NOT updated.');
              } else {
                toast.success('Restock recorded successfully!');
              }

              haptic.success();
              utils.stock.getRecentReUps.invalidate();
              utils.stock.getReUps.invalidate();
              utils.product.list.invalidate();
              setIsRestockModalOpen(false);
            } catch (error) {
              haptic.error();
              toast.error('Failed to record restock. Please try again.');
            }
          }}
          isLoading={createReUp.isPending}
        />
      </Modal>
    </div>
  );
}
